/**
 * 受領待ち / 受領済み(2026-10-15 発注者指示で作り直し。「受領」は同日「受領済み」に改称)。
 *
 * 申請者が「自分のお金がどうなっているか」をすぐ見られるようにする画面。特に、立て替えたお金が
 * いつ戻ってくるか・いまどうなっているか。
 *   - 受領待ち: まだ受け取っていないお金
 *       ・立替: 自分が支払い済みで、経理の精算がまだのもの(状況と、立替からの日数を出す)
 *       ・仮払い: 経理から仮払い金を渡されて、受け取りの記録がまだのもの(ここで記録する)
 *   - 受領: 受け取ったお金の履歴(立替の精算済み・仮払い金の受領済み)
 * 「支払い」(自分が支払わないといけないもの)は支払い画面のまま。
 * 以前の「受領」画面(受け取りを記録する作業の一覧)の作業は、受領待ちに移した。
 *
 * PCの表は見出しの項目名で並べ替え・絞り込み(申請一覧と同じ)。スマホは表が自動で縦積みカードになる。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { fetchUsers } from '@core/masters/masterRepo';
import { formatYen } from '@core/lib/money';
import { todayStr, toShortDate, tsToMillis } from '@core/lib/date';
import { useIsMobile } from '@core/lib/useIsMobile';
import { SheetTh, applySheet, countSheetFilters, type SheetColumn, type SheetFilters, type SheetSort } from '@core/components/SheetHeader';
import type { Transaction } from '@core/lib/types';
import { fetchMyMoneyBack, moneyBackDate } from './flowActions';
import { markReadForTransaction, subscribeMyUnread } from './notificationsRepo';
import ApplicationDetailModal from './ApplicationDetailModal';
import { ReceiptAttachModal, StatusBadge } from './FlowParts';

type Mode = 'waiting' | 'received';

/** 日付(YYYY-MM-DD)から今日までの日数 */
function daysSince(date: string | null | undefined): number | null {
  if (!date) return null;
  const a = new Date(`${date}T00:00:00+09:00`).getTime();
  const b = new Date(`${todayStr()}T00:00:00+09:00`).getTime();
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/** 仮払い金を渡された日(YYYY-MM-DD) */
function handedDate(t: Transaction): string {
  const ms = tsToMillis(t.cash_handed_at);
  return ms ? new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 10) : '';
}

const kindOf = (t: Transaction) => (t.payment_method === 'prepaid' ? '仮払い金' : '立替');

export default function ReceiveMoneyPage({ mode }: { mode: Mode }) {
  const { user, flowRole, isAccounting, can } = useAuth();
  const { companyName, departmentName, accountName } = useMasters();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [waiting, setWaiting] = useState<Transaction[]>([]);
  const [received, setReceived] = useState<Transaction[]>([]);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Transaction | null>(null);
  const [attaching, setAttaching] = useState<{ t: Transaction; mode: 'receive' | 'settle' } | null>(null);
  const [sheetFilters, setSheetFilters] = useState<SheetFilters>({});
  const [sheetSort, setSheetSort] = useState<SheetSort>(
    mode === 'waiting' ? { key: 'date', dir: 'asc' } : { key: 'date', dir: 'desc' },
  );

  const load = useCallback(
    async (silent = false) => {
      if (!user) return;
      try {
        const [money, users] = await Promise.all([
          fetchMyMoneyBack(user.id, user.manager_department_ids, flowRole === 'approver' || isAccounting || can('master_admin')),
          fetchUsers(),
        ]);
        setWaiting(money.waiting);
        setReceived(money.received);
        setUserNames(new Map(users.map((u) => [u.id, u.display_name])));
      } catch (e) {
        if (silent) console.error('受領の一覧の読み直しに失敗しました', e);
        else toast(e instanceof Error ? e.message : '一覧を取得できませんでした');
      } finally {
        setLoading(false);
      }
    },
    [user, flowRole, isAccounting, can, toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // お知らせが届いたとき・画面に戻ったときに読み直す(ほかの画面と同じ)
  useEffect(() => {
    if (!user) return;
    let first = true;
    const unsubscribe = subscribeMyUnread(user.id, () => {
      if (first) {
        first = false;
        return;
      }
      void load(true);
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, load]);

  const rows = mode === 'waiting' ? waiting : received;
  const name = (id: string | null | undefined) => (id ? (userNames.get(id) ?? '—') : '—');

  /** 状況(受領待ち) */
  const situation = (t: Transaction) =>
    t.status === 'cash_ready' ? '受け取ったら記録してください' : '経理の精算待ち';
  /** 日付(受領待ち=立替日・仮払い日 / 受領=精算日・受領日) */
  const dateOf = (t: Transaction) =>
    mode === 'waiting' ? (t.status === 'cash_ready' ? handedDate(t) : t.payment_date) : moneyBackDate(t);
  /** 経過(受領待ち) */
  const elapsed = (t: Transaction) => {
    const d = daysSince(dateOf(t));
    if (d == null) return '—';
    return t.status === 'cash_ready' ? `渡されてから${d}日` : `立替から${d}日`;
  };

  const columns = useMemo<SheetColumn<Transaction>[]>(() => {
    const base: SheetColumn<Transaction>[] = [
      {
        key: 'date',
        label: mode === 'waiting' ? '日付' : '受け取った日',
        text: (t) => (dateOf(t) ? toShortDate(dateOf(t)) : '—'),
        sortValue: (t) => dateOf(t),
      },
      { key: 'kind', label: '種類', text: kindOf },
      { key: 'creator', label: '申請者', text: (t) => name(t.created_by) },
      { key: 'company', label: '会社', text: (t) => companyName(t.company_id ?? 'sum') },
      { key: 'dept', label: '事業部', text: (t) => departmentName(t.department_id) },
      { key: 'account', label: '科目①', text: (t) => (t.account_id ? accountName(t.account_id) : '—') },
      { key: 'amount', label: '金額', text: (t) => formatYen(t.amount), sortValue: (t) => t.amount },
    ];
    if (mode === 'waiting') {
      base.push(
        { key: 'situation', label: '状況', text: situation },
        { key: 'elapsed', label: '経過', text: elapsed, sortValue: (t) => daysSince(dateOf(t)) ?? 0 },
      );
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, userNames, companyName, departmentName, accountName]);

  const visible = useMemo(
    () => applySheet(rows, columns, isMobile ? {} : sheetFilters, sheetSort),
    [rows, columns, isMobile, sheetFilters, sheetSort],
  );
  const total = visible.reduce((s, t) => s + t.amount, 0);

  function openDetail(t: Transaction) {
    setDetail(t);
    if (user) void markReadForTransaction(user.id, t.id);
  }

  /** 詳細の中の操作(仮払い金の受け取りの記録・旧い流れの精算受領書) */
  function detailActions(t: Transaction) {
    if (t.status === 'cash_ready' && t.created_by === user?.id) {
      return (
        <button
          className="btn-sm-ok"
          onClick={() => {
            setAttaching({ t, mode: 'receive' });
            setDetail(null);
          }}
        >
          受領を記録(記入済み受領書を添付)
        </button>
      );
    }
    if (t.advance === true && t.reimbursed_at && !t.settlement_received_at && t.paid_by === user?.id) {
      return (
        <button
          className="btn-sm-ok"
          onClick={() => {
            setAttaching({ t, mode: 'settle' });
            setDetail(null);
          }}
        >
          精算受領書を添付
        </button>
      );
    }
    return undefined;
  }

  const title = mode === 'waiting' ? '受領待ち(まだ受け取っていないお金)' : '受領済み(受け取ったお金)';
  const empty = mode === 'waiting' ? 'まだ受け取っていないお金はありません。' : '受け取ったお金の記録はまだありません。';

  return (
    <section className="screen" style={{ maxWidth: 1200 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          {title}
          {!loading && rows.length > 0 && (
            <span className="hint" style={{ fontWeight: 400 }}>
              {visible.length}件 / 合計 {formatYen(total)}円
            </span>
          )}
          {!isMobile && countSheetFilters(sheetFilters) > 0 && (
            <button className="txn-link" style={{ fontWeight: 400 }} onClick={() => setSheetFilters({})}>
              絞り込みをすべて解除({countSheetFilters(sheetFilters)}列)
            </button>
          )}
        </h3>
        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="empty">{empty}</p>
        ) : visible.length === 0 ? (
          <p className="empty">条件に合うものがありません。</p>
        ) : (
          <table className="tx-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <SheetTh
                    key={c.key}
                    column={c}
                    columns={columns}
                    rows={rows}
                    filters={sheetFilters}
                    onFilters={setSheetFilters}
                    sort={sheetSort}
                    onSort={setSheetSort}
                  />
                ))}
                {mode === 'received' && <th>状態</th>}
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.id}>
                  <td>{dateOf(t) ? toShortDate(dateOf(t)) : '—'}</td>
                  <td>{kindOf(t)}</td>
                  <td>{name(t.created_by)}</td>
                  <td className="hint">{companyName(t.company_id ?? 'sum')}</td>
                  <td>{departmentName(t.department_id)}</td>
                  <td className="hint">{t.account_id ? accountName(t.account_id) : '—'}</td>
                  <td className="num">{formatYen(t.amount)}</td>
                  {mode === 'waiting' && (
                    <td>
                      <span className="badge b-pend">{situation(t)}</span>
                    </td>
                  )}
                  {mode === 'waiting' && <td>{elapsed(t)}</td>}
                  {mode === 'received' && (
                    <td>
                      <StatusBadge t={t} />
                    </td>
                  )}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-line" style={{ padding: '4px 8px', fontSize: 11.5 }} onClick={() => openDetail(t)}>
                      {detailActions(t) ? '詳細・操作' : '詳細'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <ApplicationDetailModal
          transaction={detail}
          onClose={() => setDetail(null)}
          onChanged={() => void load()}
          actions={detailActions(detail)}
        />
      )}
      {attaching && (
        <ReceiptAttachModal
          transaction={attaching.t}
          mode={attaching.mode}
          onClose={() => setAttaching(null)}
          onDone={() => void load()}
        />
      )}
    </section>
  );
}

